<template>
  <div v-if="loading">
    <div class="skeleton-container">
      <Skeleton width="20%" height="20px" />
      <Skeleton width="35%" height="40px" />
    </div>

    <div class="skeleton-container">
      <Skeleton width="40%" height="20px" />
      <Skeleton width="60%" height="40px" />
    </div>

    <div class="skeleton-container">
      <Skeleton width="40%" height="20px" />
      <Skeleton width="60%" height="40px" />
    </div>
    <div class="skeleton-container">
      <Skeleton width="25%" height="20px" />
      <Skeleton width="75%" height="40px" />
    </div>
    <div class="skeleton-container">
      <Skeleton width="25%" height="20px" />
      <Skeleton width="75%" height="40px" />
    </div>
  </div>
  <div v-else>
    <div class="group">
      Upload New CSV
      <FileUpload style="flex:4;" mode="basic" name="csv[]" accept=".csv,text/csv" :maxFileSize="1000000" @select="onUpload($event)" :auto="true" chooseLabel="Choose File" />
    </div>

    <div class="group">
      Select Existing CSV
      <Dropdown style="flex: 4;" optionLabel="label" v-model="chosenCsv" :options="fileNames" :loading="loadingNewUpload" placeholder="Select an item" />
    </div>

    <div class="group">
      Default Text
      <InputText v-model="placeholder"  style="width: 200px;" />
    </div>


    <div class="style-section" style="display: flex; align-items: center; margin-top: 20px">
      <span class="label-long" style="margin-right:10px;">Placeholder Color</span>
      <div style="width: 100px; display: inline-block">
        <ColorButton @click="colorPickerVisible.toggle($event)" :color="placeholderColor" style="flex: 3"  />
        <OverlayPanel ref="colorPickerVisible">
          <ColorPicker v-model="placeholderColor"
                       @edit-theme="$refs.colorPickerVisible.toggle(false); themeDialogVisible = true"
          />
        </OverlayPanel>
      </div>
    </div>

    <div class="group">
      Parent
      <Dropdown style="flex: 4;" optionLabel="label" v-model="dependsOn" :options="dependsAndLinkOptions" placeholder="Parent Dropdown" />
    </div>

    <div class="group">
      Link
      <Dropdown style="flex: 4;" optionLabel="label" v-model="link" :options="dependsAndLinkOptions" placeholder="Link Dropdown" />
    </div>

    <div v-if="dataLoaded">
      <div class="group">
        Display Column
        <Dropdown style="flex: 4;" optionLabel="label" v-model="displayColumn" :options="columnNames" :loading="loadingColumnNames" placeholder="Select a Column Header" />
      </div>

      <div class="group">
        Notes
        <InputText v-model="notes"  style="width: 200px;" />
      </div>

      <div class="switch-container">
        <label for="firstItemSwitch" style="width: 110px;">Default First Item</label>
        <InputSwitch class="smaller-switch" v-model="defaultOn" id="firstItemSwitch" />
      </div>

      <div class="switch-container">
        <label for="readOnlySwitch" style="width: 110px;">Read Only</label>
        <InputSwitch class="smaller-switch" v-model="readOnly" id="readOnlySwitch" />
      </div>

      <div v-if="selectType === 'Button'" class="switch-container">
        <label for="readOnlySwitch" style="width: 110px;">Add Confirmation Message</label>
        <InputSwitch class="smaller-switch" v-model="confirmDialog" id="readOnlySwitch" />
      </div>

      <div v-if="confirmDialog && selectType === 'Button'" style="padding-top:20px;">
        <div>Message</div>
        <Editor ref="editor" @blur="" v-model="confirmationMessage" :modules="toolbarOptions" editorStyle="height: 150px"/>
      </div>

      <div style="margin-top: 20px; ">Type</div>
      <SelectButton style="margin-bottom: 20px" v-model="selectType" :options="selectTypeOptions"/>

      <div style="margin-top: 20px; ">Name Position</div>
      <SelectButton style="margin-bottom: 20px" v-model="labelPosition" :options="positionOptions"/>

      <span class="label">Color</span>
      <ColorButton :width="`100px`" :color="buttonColor ?? '#000'" @click="$refs.buttonPickerVisible.toggle($event)" style="width:200px;"/>
      <OverlayPanel ref="buttonPickerVisible">
        <ColorPicker v-model="buttonColor"/>
      </OverlayPanel>

      <div class="group" v-if="selectType === 'Auto Complete'">
        <div style="width: 175px">Auto Complete Button Color</div>
        <ColorButton :width="`100px`" :color="autocompleteButtonColor" @click="$refs.colorPickerVisible.toggle($event)" style="width: 120px;margin-left: 10px;"/>
        <OverlayPanel ref="colorPickerVisible">
          <ColorPicker v-model="autocompleteButtonColor"/>
        </OverlayPanel>
      </div>

      <Idot/>

      <div v-if="notIsDependsOn" style="margin-top: 20px;">
        <div v-for="options in matchColumnNames" :key="options.value" style="padding: 10px; margin-bottom: 10px; border: solid 1px #DDDDDD;">
          <div style="width: 270px; display: inline-block">
            {{ options.label }} <div v-if="options.example != null">(example: {{ options.example }})</div>
          </div>

          <div style="width: 270px; display: flex">
            <ValueModelAutoComplete @change="setHookId($event, options)" :hookId="getHookId(options.label)"  style="flex:5;" />
          </div>
          <div style="width: 270px; display: inline-block">
            <SelectButton v-model="csvValueNotes[options.label]"
                          @update:modelValue="value => updateCsvValueNotes({ key: options.label, value: value })"
                          :options="mappingsOptions"/>
          </div>
        </div>
      </div>
    </div>
  </div>

</template>

<script setup lang="ts">
import {ref, onMounted, watch, watchEffect, computed, reactive, nextTick} from 'vue'
import useFileService from '../hooks/useFileService'
import {useCsvStore, useMainStore, useSlidesStore, useWidgetStore} from '@/store'
import {storeToRefs} from 'pinia'
import ValueModelAutoComplete from "./ValueModelAutoComplete.vue";
import InputText from 'primevue/inputtext';
import SelectButton from "primevue/selectbutton";
import ColorPicker from "@/components/ColorPicker/index.vue";
import {PPTElementShadow} from "@/types/slides";
import ColorButton from "@/views/Editor/Toolbar/common/ColorButton.vue";
import Idot from "@/views/Editor/Toolbar/common/IdotStyle.vue";
import {getValuePropositionAPI} from "@/services/value-proposition-api";

import { debounce} from 'lodash'
const { slides } = storeToRefs(useSlidesStore())
const { fileNames, csvFileData } = storeToRefs(useCsvStore())

const api = useFileService()

const widgetStore = useWidgetStore()
const { selectedWidget } = storeToRefs(widgetStore)

const slidesStore = useSlidesStore()
const { valueModelId } = storeToRefs(slidesStore)

const availableFiles = ref()
const loading = ref(false)
const loadingNewUpload = ref(false)
const loadingColumnNames = ref(false)

const selectTypeOptions = ref([
  'Dropdown',
  //'Dropdown w/ Search',
  'Auto Complete',
  'Button'
])

const createPositionComputed = (positionKey) => {
  return computed({
    get() {
      const value = selectedWidget.value?.[positionKey] || 'top';
      switch (value) {
        case 'top': return "Top";
        case 'bottom': return "Bottom";
        case 'left': return "Left";
        case 'right': return "Right";
        case 'none': return "None";
        default: return "Top";
      }
    },
    set(value) {
      switch (value) {
        case 'Top': selectedWidget.value[positionKey] = 'top'; break;
        case 'Bottom': selectedWidget.value[positionKey] = 'bottom'; break;
        case 'Left': selectedWidget.value[positionKey] = 'left'; break;
        case 'Right': selectedWidget.value[positionKey] = 'right'; break;
        case 'None': selectedWidget.value[positionKey] = 'none'; break;
      }
    }
  });
};

const labelPosition = createPositionComputed('labelPosition');

const positionOptions = ref([
  'Top',
  'Bottom',
  'Left',
  'Right',
  'None'
])


const buttonColor = computed({
  get() {
    return selectedWidget.value?.buttonColor ?? '#2196F3'
  },
  set(val) {
    selectedWidget.value.buttonColor = val
  }
})

const {themeDialogVisible} = storeToRefs(useMainStore())

const autocompleteButtonColor = computed({
  get() {
    return selectedWidget.value?.autocompleteButtonColor ?? '#2196F3'
  },
  set(val) {
    selectedWidget.value.autocompleteButtonColor = val
  }
})

const columnNames = computed(() => {
  const data = csvFileData.value[csvFilename.value]
  if (!data) {
    return []
  }
  return [...new Set(data.flatMap(obj => Object.keys(obj)))].map((key, index) => ({
    label: key,
    value: index,
    hookId: '--',
  }));
})

const mappingsOptions = ref([
  'Value',
  'Notes'
])

const csvFilename = computed(() => selectedWidget.value?.csv?.fileName?.label)

const notIsDependsOn = computed(() => {
  return !dependsOn.value || !dependsOn.value.code
})

const colorPickerVisible = ref()

const dataLoaded = computed(() => {
  if (!csvFileData.value || !csvFilename.value) {
    return false
  }
  return csvFileData.value[csvFilename.value]?.length > 0
})

const dependsAndLinkOptions = computed(() => {
  const dropdowns = []
  const notesDropdowns = []

  for (const slide of slides.value) {
    for (const element of slide.elements) {
      if (element.type !== 'slidelayout') {
        continue;
      }

      for (const row of element.layout.children) {
        for (const column of row.children) {
          if (column.dragDropElement && column.dragDropElement.widgets) {
            for (const widget of column.dragDropElement.widgets) {
              for (const child of widget.children) {
                if (child.type === 'csv_selector' && child.id !== selectedWidget.value?.id) {
                  if (child.notes) {
                    notesDropdowns.push({
                      code: child.id,
                      label: child.notes
                    })
                  }
                  else {
                    dropdowns.push({
                      code: child.id,
                      label: `${child.chosenCsv?.label} (${child.displayColumn?.label}) "${child.placeholder ?? 'No default text'}"`
                    })
                  }
                }
              }
            }
          }
        }
      }
    }
  }

  return [
      ...[{ code: null, label: 'None'}],
    ...notesDropdowns.sort((a, b) => a.label.localeCompare(b.label)),
    ...dropdowns.sort((a, b) => a.label.localeCompare(b.label))
  ]
})

const editor = ref(null);


watch(selectedWidget, (newVal) => {
  if (newVal) {
    setTimeout(() => {
      setupQuill()
    }, 500)
  }
})

const confirmDialog = computed({
  get() {
    return selectedWidget.value?.confirmDialog ?? false
  },
  set(val) {
    selectedWidget.value.confirmDialog = val
  }
})

let tempConfirm = null
let confirmTimer = null

const confirmationMessage = computed({
  get() {
    return selectedWidget.value?.confirmationMessage ?? ''
  },
  set: val => {
    tempConfirm = val
    if (!confirmTimer) {
      confirmTimer = setTimeout(() => {
        confirmTimer = null
        selectedWidget.value && (selectedWidget.value.confirmationMessage = tempConfirm)
      }, 1000)
    }
  }
})

const setupQuill = () => {
  if (confirmationMessage.value) {
    const processedContent = processNewLines(confirmationMessage.value);
    confirmationMessage.value = processedContent

    nextTick(() => {
      setTimeout(() => {
        const quillInstance = editor.value.quill
        const editorRoot = quillInstance.root;

        quillInstance.clipboard.dangerouslyPasteHTML(processedContent);
        editorRoot.classList.remove('ql-blank');
      }, 500)
    })
  }
}

function processNewLines(content) {
  if (!/^<p>.*<\/p>$/s.test(content)) {
    content = `<p>${content}</p>`;
  }

  content = content.replace(/\n/g, '</p><p>');

  return content;
}

const toolbarOptions = {
  toolbar: [
    ['bold', 'italic', 'underline'],
    ['link']
  ]
}


const chosenCsv = computed({
  get: () => selectedWidget.value?.chosenCsv,
  set: val => {
    selectedWidget.value && (selectedWidget.value.chosenCsv = val)
  }
})

const dependsOn = computed({
  get: () => {
    if (!selectedWidget.value?.dependsOn?.code) {
      return [];
    }
    return dependsAndLinkOptions.value.find(entry => entry.code === selectedWidget.value.dependsOn.code)
  },
  set: val => {
    selectedWidget.value && (selectedWidget.value.dependsOn = val)
  }
})

const link = computed({
  get: () => {
    if (!selectedWidget.value?.link?.code) {
      return [];
    }
    return dependsAndLinkOptions.value.find(entry => entry.code === selectedWidget.value.link.code)
  },
  set: val => {
    selectedWidget.value && (selectedWidget.value.link = val)
  }
})

const displayColumn = computed({
  get: () => selectedWidget.value?.displayColumn,
  set: val => {
    selectedWidget.value && (selectedWidget.value.displayColumn = val)
  }
})

const placeholderColor = computed({
  get: () => selectedWidget.value?.style?.placeholderColor ?? 'transparent',
  set: val => selectedWidget.value && (selectedWidget.value.style.placeholderColor = val)
})

const csvValueNotes = computed(() => selectedWidget.value.csvValueNotes ?? {})



const updateCsvValueNotes = (update) => {
  if (!selectedWidget.value.csvValueNotes) {
    selectedWidget.value.csvValueNotes = {}
  }
  selectedWidget.value.csvValueNotes[update.key] = update.value
}
const matchColumnNames = computed(() => {

    const columns = [...new Set(csvFileData.value[csvFilename.value].flatMap(obj => Object.keys(obj)))].map((key, index) => ({
      label: key,
      value: index,
      hookId: '--',
      example: null
    }))

  if (csvFileData.value[csvFilename.value].length > 0) {
    for (const column of columns) {
      column.example = csvFileData.value[csvFilename.value][0][column.label]
    }
  }

  return columns
})

const placeholder = computed({
  get: () => selectedWidget.value?.placeholder,
  set: val => {
    selectedWidget.value && (selectedWidget.value.placeholder = val)
  }
})

// TODO figure out why typing notes is so slow?  reactive updates on the tile?
let tempNotes = null
let notesTimer = null
const notes = computed({
  get: () => selectedWidget.value?.notes,
  set: val => {
    tempNotes = val
    if (!notesTimer) {
      notesTimer = setTimeout(() => {
        notesTimer = null
        selectedWidget.value && (selectedWidget.value.notes = tempNotes)
      }, 1000)
    }
  }
})


const defaultOn = computed({
  get: () => {
    return selectedWidget.value.selectFirst ?? false
  },
  set: val => {
    selectedWidget.value.selectFirst = val
  }
})

const readOnly = computed({
  get: () => {
    return selectedWidget.value.readOnly ?? false
  },
  set: val => {
    selectedWidget.value.readOnly = val
  }
})


const selectType = computed({
  get: () => {
    switch (selectedWidget.value.selectType) {
      case 'search':
        return 'Dropdown w/ Search'
      case 'autocomplete':
        return 'Auto Complete'
      case 'button':
        return 'Button'
    }
    return 'Dropdown'
  },
  set: (val) => {
    if (val === 'Dropdown w/ Search') selectedWidget.value.selectType = 'search'
    if (val === 'Auto Complete') selectedWidget.value.selectType = 'autocomplete'
    if (val === 'Dropdown') selectedWidget.value.selectType = 'dropdown'
    if (val === 'Button') selectedWidget.value.selectType = 'button'
  }
})


watch(selectedWidget, (newVal) => {
  if (newVal) {
    setTimeout(() => {
      setupQuill()
    }, 500)
  }
})




onMounted(async () => {

  if (selectedWidget.value.csv && selectedWidget.value.csv.fileName && !csvFileData.value[csvFilename.value]) {
    // First thread in will set this so that the others aren't concurrently doing it
    csvFileData.value[csvFilename.value] = []
    const preSignedUrl = await api.getFileSignedUrl(selectedWidget.value.csv.fileName.label)
    csvFileData.value[csvFilename.value] = await api.downloadAndParseCsv(preSignedUrl)
  }

  setWidgetChosenCsv()
  if (!fileNames.value) {
    await getFileTitles()
  }

  if (selectedWidget.value.csvFilters) {
    for (const key of Object.keys(selectedWidget.value.csvFilters)) {
      filters[key] = selectedWidget.value.csvFilters[key]
    }
  }

  setTimeout(() => {
    setupQuill()
  }, 100)
})

watch(chosenCsv, async (newValue) => {
  if (newValue) {

    if (!csvFileData.value[newValue.label]) {
      // First thread in will set this so that the others aren't concurrently doing it
      csvFileData.value[newValue.label] = []
      const preSignedUrl = await api.getFileSignedUrl(newValue.label)
      csvFileData.value[newValue.label] = await api.downloadAndParseCsv(preSignedUrl)
    }

    setWidgetChosenCsv()
  }
})

const isEmptyObject = (obj: any) => Object.entries(obj).length === 0

const getFileTitles = async () => {
  fileNames.value = []
  availableFiles.value = await api.getFileTitles()
  fileNames.value = availableFiles.value.map((item: any) => ({ label: item.title, value: item.id }))
}

const setWidgetChosenCsv = () => {
  const { csv } = selectedWidget.value
  csv.fileName = chosenCsv.value
}

const getHookId = (column: string) => {
  if (selectedWidget.value.csvMappings) {
    return selectedWidget.value.csvMappings[column] ?? ''
  }
}

const setHookId = (valueModel: any, column: any) => {
  if (typeof valueModel.value === 'object') {

    if (!selectedWidget.value.csvMappings) {
      selectedWidget.value.csvMappings = {}
    }
    selectedWidget.value.csvMappings[column.label] = valueModel.value.hookId
  }
}

const saveFiles = async (file) => {
  const signedUrl = await getSignedURL(file, 'main', 'csv', '/csv')
  const xhr = new XMLHttpRequest();
  xhr.open('PUT', signedUrl);

  xhr.setRequestHeader('Content-Type', 'text/csv');
  try {
    await new Promise((resolve, reject) => {
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new Error('Failed to upload'));
        }
      };
      xhr.onerror = () => reject(new Error('Failed to upload'));
      xhr.send(file);
    });

    setTimeout(async () => {
      await getFileTitles()
      loadingNewUpload.value = false
    }, 8000)
  } catch (error) {
    console.error('Error:', error);
  }
};

const getSignedURL = async (file, space, currentFolder, breadcrumbItems) => {
  const url = `${import.meta.env.VITE_APP_ENV_SERVER}/lpi-service/job-service/create-signed-url-and-job`;
  const data = {
    eventType: 'file-service-upload',
    filePath: file.name,
    contentType: file.type,
    jobDetails: {
      space: space,
      currentFolder: currentFolder,
      s3Prefix: breadcrumbItems,
      filePath: file.name,
      vmId: valueModelId.value
    }
  };

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      console.error("Failed to send data:", response.statusText);
    }

    const parsedResponse = await response.json();
    return parsedResponse.putEndpoint;
  } catch (error) {
    console.error("Error sending data:", error);
    throw new Error('/');
  }
}

const onUpload = async (event) => {
  loadingNewUpload.value = true
  await saveFiles(event.files[0])
}

</script>

<style scoped>
:deep(ul) {
  padding: 0 !important;
  margin-bottom: 0 !important;
}

.p-autocomplete > .p-inputtext {
  padding: 0.5rem 0.5rem 0.5rem 1rem !important;
}

.dropdown-container label {
  margin-right: 10px; /* Adjust the margin as needed */
}

.group {
  margin-top: 10px;
  display: flex;
  align-items: center;
  column-gap: 10px;
}

.skeleton-container {
  padding-top: 10px;
  display: flex;
  gap: 10px; /* Adjust the gap between skeletons */
}

.smaller-switch {
  transform: scale(0.6);
  display: inline-block;
  vertical-align: middle;
}

.smaller-switch {
  transform: scale(0.6);
}

.switch-container {
  margin-top: 10px;
  display: flex;
  align-items: center;
  gap: 10px;
}

:deep(.p-editor-toolbar) {
  display: none !important;
}

</style>
